import { Injectable, signal, computed, OnDestroy, inject } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subject, takeUntil } from 'rxjs';

/**
 * Servicio centralizado de responsive/breakpoints.
 * Reemplaza el uso directo de BreakpointObserver en cada componente.
 *
 * Uso:
 *   responsive = inject(ResponsiveService);
 *   // En template: @if (responsive.isMobile()) { ... }
 *   // En TS: if (this.responsive.isMobile()) { ... }
 */
@Injectable({ providedIn: 'root' })
export class ResponsiveService implements OnDestroy {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroy$ = new Subject<void>();

  // Signals de estado
  private readonly _isXSmall = signal(false);
  private readonly _isSmall = signal(false);
  private readonly _isMedium = signal(false);
  private readonly _isLarge = signal(false);
  private readonly _isXLarge = signal(false);
  // Boundary canonica del rediseño: mobile = <768px (unifica gap entre CDK Handset=599 y 959 legacy)
  private readonly _isMobileUnified = signal(false);

  // --- Breakpoints individuales (readonly) ---
  readonly isXSmall = this._isXSmall.asReadonly();
  readonly isSmall = this._isSmall.asReadonly();
  readonly isMedium = this._isMedium.asReadonly();
  readonly isLarge = this._isLarge.asReadonly();
  readonly isXLarge = this._isXLarge.asReadonly();

  // --- Breakpoints compuestos ---
  /** Mobile: < 768px (breakpoint canonico del rediseño) */
  readonly isMobile = computed(() => this._isMobileUnified());

  /** Tablet: >= 768px && < 1280px — usar CDK Medium para rango superior */
  readonly isTablet = computed(() => !this._isMobileUnified() && this._isMedium());

  /** Desktop: Large o XLarge (>= 1280px) */
  readonly isDesktop = computed(() => this._isLarge() || this._isXLarge());

  /** Handset: alias de isMobile (<768px) */
  readonly isHandset = computed(() => this._isMobileUnified());

  /** Mobile o Tablet (< 1280px) */
  readonly isMobileOrTablet = computed(() => !this.isDesktop());

  // --- Helpers para dialogs ---
  /** Ancho recomendado para dialogs según breakpoint */
  readonly dialogWidth = computed(() => {
    if (this._isXSmall()) return '100vw';
    if (this._isSmall()) return '95vw';
    if (this._isMedium()) return '80vw';
    return '600px';
  });

  /** Config para dialog fullscreen en mobile */
  readonly dialogConfigMobile = computed(() => {
    if (this.isMobile()) {
      return {
        width: '100vw',
        maxWidth: '100vw',
        height: '100vh',
        maxHeight: '100vh',
        panelClass: 'dialog-fullscreen-mobile',
      };
    }
    return {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
    };
  });

  /** Config para dialog mediano (forms) */
  readonly dialogConfigForm = computed(() => {
    if (this.isMobile()) {
      return {
        width: '100vw',
        maxWidth: '100vw',
        height: '100vh',
        maxHeight: '100vh',
        panelClass: 'dialog-fullscreen-mobile',
      };
    }
    return {
      width: '700px',
      maxWidth: '90vw',
      maxHeight: '90vh',
    };
  });

  /** Config para dialog grande (tablas, detalles) */
  readonly dialogConfigLarge = computed(() => {
    if (this.isMobile()) {
      return {
        width: '100vw',
        maxWidth: '100vw',
        height: '100vh',
        maxHeight: '100vh',
        panelClass: 'dialog-fullscreen-mobile',
      };
    }
    return {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
    };
  });

  constructor() {
    this.breakpointObserver
      .observe([
        Breakpoints.XSmall,   // < 600px
        Breakpoints.Small,    // 600px - 959px
        Breakpoints.Medium,   // 960px - 1279px
        Breakpoints.Large,    // 1280px - 1919px
        Breakpoints.XLarge,   // >= 1920px
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this._isXSmall.set(result.breakpoints[Breakpoints.XSmall]);
        this._isSmall.set(result.breakpoints[Breakpoints.Small]);
        this._isMedium.set(result.breakpoints[Breakpoints.Medium]);
        this._isLarge.set(result.breakpoints[Breakpoints.Large]);
        this._isXLarge.set(result.breakpoints[Breakpoints.XLarge]);
      });

    // Boundary canonica 768px (unificada con SCSS $breakpoint-md)
    this.breakpointObserver
      .observe(['(max-width: 767.98px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this._isMobileUnified.set(result.matches);
      });
  }

  /** Columnas recomendadas para grids según viewport */
  gridCols(options?: { mobile?: number; tablet?: number; desktop?: number }): number {
    const { mobile = 1, tablet = 2, desktop = 3 } = options ?? {};
    if (this.isMobile()) return mobile;
    if (this.isTablet()) return tablet;
    return desktop;
  }

  /** Page size recomendado para paginadores */
  pageSize(options?: { mobile?: number; tablet?: number; desktop?: number }): number {
    const { mobile = 10, tablet = 15, desktop = 25 } = options ?? {};
    if (this.isMobile()) return mobile;
    if (this.isTablet()) return tablet;
    return desktop;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
